import { Component, inject, input, OnInit, output } from "@angular/core";
import { FormGroup, Validators, FormControl, ReactiveFormsModule, FormsModule } from "@angular/forms";

import { TranslatePipe } from "@ngx-translate/core";
import { RouterLink } from "@angular/router";
import { BsDatepickerDirective, BsLocaleService } from "ngx-bootstrap/datepicker";
import moment from "moment";
import { defineLocale } from "ngx-bootstrap/chronos";
import { deLocale } from "ngx-bootstrap/locale";
import { CountryDto } from "gast-common-ng2";

interface LoginForm {
  login: FormControl<string>;
  password: FormControl<string>;
}

interface LoginFormValue {
  login: string;
  password: string;
}

interface RegistrationForm {
  salutation: FormControl<string>;
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  birthday: FormControl<string>;
  countryOfOrigin: FormControl<null | string>;
  email: FormControl<string>;
  loginRegistration: FormControl<string>;
  passwordRegistration: FormControl<string>;
  newsletter: FormControl<string>;
  dataSecurity: FormControl<string>;
  gender: FormControl<string>;
}

interface RegistrationFormValue {
  salutation: string;
  firstName: string;
  lastName: string;
  birthday: string;
  countryOfOrigin: null | string;
  email: string;
  loginRegistration: string;
  passwordRegistration: string;
  newsletter: string;
  dataSecurity: string;
  gender: string;
}

@Component({
  selector: "portal-login",
  templateUrl: "./login.component.html",
  styleUrls: ["./login.component.scss"],
  standalone: true,
  imports: [ReactiveFormsModule, TranslatePipe, RouterLink, BsDatepickerDirective, FormsModule],
})
export class LoginComponent implements OnInit {
  readonly #bsLocaleService = inject(BsLocaleService);
  readonly isForgotPasswordHref = input(true);
  readonly forgotPasswordUrl = input("#");
  readonly dataPrivacyProtectionUrl = input("#");
  readonly hideRegister = input(false);
  readonly hideBlockquote = input(false);
  readonly countries = input<CountryDto[]>([]);
  readonly locale = "de";

  readonly loginPressed = output<LoginFormValue>();
  readonly registerPressed = output<RegistrationFormValue>();

  readonly loginForm = new FormGroup<LoginForm>({
    login: new FormControl("", { nonNullable: true }),
    password: new FormControl("", { nonNullable: true }),
  }) as FormGroup<LoginForm>;

  readonly registrationForm = new FormGroup<RegistrationForm>({
    salutation: new FormControl("", { validators: Validators.required, nonNullable: true }),
    firstName: new FormControl("", { nonNullable: true }),
    lastName: new FormControl("", { nonNullable: true }),
    birthday: new FormControl("", { nonNullable: true }),
    countryOfOrigin: new FormControl(null, { nonNullable: true }),
    email: new FormControl("", { nonNullable: true }),
    loginRegistration: new FormControl("", { nonNullable: true }),
    passwordRegistration: new FormControl("", { nonNullable: true }),
    newsletter: new FormControl("", { nonNullable: true }),
    dataSecurity: new FormControl("", { nonNullable: true }),
    gender: new FormControl("", { nonNullable: true }),
  });

  ngOnInit() {
    defineLocale(this.locale, deLocale);
    this.#bsLocaleService.use(this.locale);
  }

  showPassword = false;

  onLogin(): void {
    this.loginPressed.emit(this.loginForm.value as LoginFormValue);
  }

  /** hide and show password */
  showPasswordChanged() {
    this.showPassword = !this.showPassword;
  }

  /**
   *  gender is set based on salutation
   *  TODO: Gender input checkboxes ( not yet specified )
   */
  onRegister(): void {
    const salutation = this.registrationForm.value.salutation;
    let gender = "";
    switch (salutation) {
      case "MR":
        gender = "MALE";
        break;
      case "MRS":
        gender = "FEMALE";
        break;
      case "NONE":
        gender = "INTERSEX";
        break;
      default:
        gender = "MALE";
        break;
    }
    this.registrationForm.controls.gender.setValue(gender);
    this.registerPressed.emit(this.registrationForm.value as RegistrationFormValue);
  }

  get login(): FormControl<string> {
    return this.loginForm.controls.login;
  }
  get password(): FormControl<string> {
    return this.loginForm.controls.password;
  }

  get salutation(): FormControl<string> {
    return this.registrationForm.controls.salutation;
  }
  get firstName(): FormControl<string> {
    return this.registrationForm.controls.firstName;
  }
  get lastName(): FormControl<string> {
    return this.registrationForm.controls.lastName;
  }
  get birthday(): FormControl<string> {
    return this.registrationForm.controls.birthday;
  }
  get email(): FormControl<string> {
    return this.registrationForm.controls.email;
  }
  get countryOfOrigin(): FormControl<string | null> {
    return this.registrationForm.controls.countryOfOrigin;
  }
  get loginRegistration(): FormControl<string> {
    return this.registrationForm.controls.loginRegistration;
  }
  get passwordRegistration(): FormControl<string> {
    return this.registrationForm.controls.passwordRegistration;
  }
  get newsletter(): FormControl<string> {
    return this.registrationForm.controls.newsletter;
  }
  get dataSecurity(): FormControl<string> {
    return this.registrationForm.controls.dataSecurity;
  }

  handleBirthdayChange(date: Date) {
    this.birthday.setValue(moment(date).format("DD.MM.YYYY"));
  }
}
